package org.gymly.ui.admin

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.outlined.Block
import androidx.compose.material.icons.outlined.CheckCircle
import androidx.compose.material.icons.outlined.Notifications
import androidx.compose.material.icons.outlined.Refresh
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.Checkbox
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.OutlinedTextFieldDefaults
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.PlainTooltip
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.material3.TooltipBox
import androidx.compose.material3.TooltipDefaults
import androidx.compose.material3.rememberTooltipState
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import org.gymly.data.AdminRepo
import org.gymly.model.AdminClientTermsMonitoring
import org.gymly.model.AdminClientTermsRow
import org.gymly.model.AdminPendingApprovalException
import org.gymly.model.AdminTermsNoticeChannels
import org.gymly.model.AdminTermsNoticeProgress
import org.gymly.theme.AdminColors
import org.gymly.theme.GymlyColors
import org.gymly.ui.components.GymlyLoadingCenter
import org.gymly.ui.components.GymlyTextField
import org.gymly.util.userMessage

private const val CANAL_CORREO = "EMAIL"
private const val CANAL_WHATSAPP = "WHATSAPP"

/** Igual que el backend: el canal se pausa tras este número de fallos definitivos seguidos. */
private const val PAUSA_TRAS_FALLOS = 10

/** Igual que Clientes: por debajo de este ancho el directorio pasa a tarjetas apiladas. */
private val COMPACT_BREAKPOINT = 700.dp

/** Filtro del directorio de socios activos. */
private enum class TermsFilter(val label: String) {
    TODOS("Todos"),
    PENDIENTES("Pendientes"),
    ACEPTADOS("Aceptaron"),
    RECHAZADOS("Rechazaron"),
    SUSPENDIDOS("Suspendidos"),
}

/**
 * Configuración → Monitoreo de términos. Solo socios activos.
 * Ver: directorio con estado, contacto y filtros. Usar: avisos, reactivar, reenviar y suspender.
 * Los avisos van a una cola: el progreso se actualiza solo. Sin "Aprobar", el lote queda en Aprobaciones.
 */
@Composable
fun AdminTermsMonitoringPanel() {
    var data by remember { mutableStateOf<AdminClientTermsMonitoring?>(null) }
    var loadError by remember { mutableStateOf<String?>(null) }
    var notice by remember { mutableStateOf<String?>(null) }
    var actionError by remember { mutableStateOf<String?>(null) }
    var busyId by remember { mutableStateOf<String?>(null) }
    var filter by remember { mutableStateOf(TermsFilter.TODOS) }
    var query by remember { mutableStateOf("") }
    var suspendTarget by remember { mutableStateOf<AdminClientTermsRow?>(null) }
    var suspendNote by remember { mutableStateOf("") }
    var noticeTargets by remember { mutableStateOf<List<AdminClientTermsRow>?>(null) }
    var noticeScopePendientes by remember { mutableStateOf(false) }
    var batchId by remember { mutableStateOf<String?>(null) }
    var progress by remember { mutableStateOf<AdminTermsNoticeProgress?>(null) }
    var reload by remember { mutableStateOf(0) }
    val scope = rememberCoroutineScope()

    LaunchedEffect(reload) {
        loadError = null
        try {
            data = AdminRepo.api.fetchClientTermsMonitoring()
        } catch (e: Throwable) {
            loadError = e.userMessage("No pudimos cargar el monitoreo de términos.")
        }
    }

    // Progreso del lote en la cola: se consulta cada 3 segundos hasta que no quede nada pendiente.
    LaunchedEffect(batchId) {
        val id = batchId ?: return@LaunchedEffect
        while (true) {
            val p = runCatching { AdminRepo.api.termsNoticeProgress(id) }.getOrNull()
            progress = p
            if (p == null || p.done) break
            delay(3_000)
        }
    }

    fun run(row: AdminClientTermsRow, op: String, motivo: String? = null) {
        scope.launch {
            busyId = row.id
            actionError = null
            notice = null
            try {
                AdminRepo.api.applyClientTermsAction(row.id, op, motivo)
                notice = "Hecho: ${row.name}."
                reload++
            } catch (e: AdminPendingApprovalException) {
                notice = e.message
            } catch (e: Throwable) {
                actionError = e.userMessage("No se pudo completar la acción.")
            } finally {
                busyId = null
            }
        }
    }

    fun resumeChannel(canal: String) {
        scope.launch {
            actionError = null
            notice = null
            try {
                data = AdminRepo.api.resumeTermsNoticeChannel(canal)
                notice = "Canal reanudado. Los avisos pendientes se enviarán en el siguiente ciclo."
            } catch (e: Throwable) {
                actionError = e.userMessage("No se pudo reanudar el canal.")
            }
        }
    }

    fun sendNotices(targets: List<AdminClientTermsRow>, pendientes: Boolean, canales: List<String>) {
        scope.launch {
            actionError = null
            notice = null
            try {
                val r = AdminRepo.api.sendClientTermsNotices(
                    scope = if (pendientes) "PENDIENTES" else "IDS",
                    clientIds = if (pendientes) null else targets.map { it.id },
                    channels = canales,
                )
                batchId = r.batchId
                progress = null
                notice = "En cola: ${r.queued} aviso(s). Sin contacto: ${r.noContact}. " +
                    "En plazo: ${r.withinCooldown}. Ya en cola: ${r.alreadyQueued}."
            } catch (e: AdminPendingApprovalException) {
                notice = e.message
            } catch (e: Throwable) {
                actionError = e.userMessage("No se pudo encolar el aviso.")
            }
        }
    }

    suspendTarget?.let { target ->
        AlertDialog(
            onDismissRequest = { suspendTarget = null },
            title = { Text("Suspender a ${target.name}", fontWeight = FontWeight.Bold) },
            text = {
                Column {
                    Text(
                        "Úsalo por una razón administrativa del gimnasio (no por los términos). El motivo queda registrado.",
                        fontSize = 13.sp,
                    )
                    Spacer(Modifier.height(10.dp))
                    GymlyTextField(
                        value = suspendNote,
                        onValueChange = { suspendNote = it },
                        placeholder = "Motivo (obligatorio)",
                    )
                }
            },
            confirmButton = {
                Button(
                    onClick = {
                        val motivo = suspendNote.trim()
                        if (motivo.isNotEmpty()) {
                            val t = target
                            suspendTarget = null
                            suspendNote = ""
                            run(t, "SUSPEND", motivo)
                        }
                    },
                    enabled = suspendNote.isNotBlank(),
                    colors = ButtonDefaults.buttonColors(containerColor = GymlyColors.Error),
                ) { Text("Suspender") }
            },
            dismissButton = { TextButton(onClick = { suspendTarget = null }) { Text("Cancelar") } },
        )
    }

    noticeTargets?.let { targets ->
        val canales = data?.channels
        NoticeDialog(
            count = targets.size,
            pendientes = noticeScopePendientes,
            channels = canales,
            onConfirm = { selected ->
                noticeTargets = null
                sendNotices(targets, noticeScopePendientes, selected)
            },
            onDismiss = { noticeTargets = null },
        )
    }

    val current = data
    when {
        current == null && loadError == null -> GymlyLoadingCenter()
        loadError != null -> Column(Modifier.padding(16.dp)) {
            Text(loadError!!, color = AdminColors.Error)
            Spacer(Modifier.height(8.dp))
            Button(onClick = { reload++ }) { Text("Reintentar") }
        }
        current != null && !current.available -> Column(Modifier.padding(16.dp)) {
            Text("Monitoreo no disponible", fontWeight = FontWeight.Bold, fontSize = 16.sp, color = AdminColors.Text)
            Spacer(Modifier.height(6.dp))
            Text(
                "Actívalo en Empresa (Monitoreo de Términos y condiciones). Requiere licencia de portal o app.",
                color = AdminColors.TextMuted,
                fontSize = 13.sp,
            )
        }
        current != null -> {
            val activos = current.members.filter { it.active }
            val pendientes = activos.filter { it.status == "PENDIENTE" }
            val canAvisar = current.canOperate && (current.channels.email || current.channels.whatsapp)
            val filtrados = activos.filter { filter.incluye(it) && coincide(it, query) }

            Column(Modifier.fillMaxSize().verticalScroll(rememberScrollState()).padding(16.dp)) {
                Text(
                    "Monitoreo de términos de socios",
                    fontSize = 24.sp,
                    fontWeight = FontWeight.Bold,
                    color = AdminColors.Text,
                )
                Text(
                    "Versión vigente: ${current.currentVersion}. Los socios que rechazan quedan suspendidos hasta que los reactives.",
                    color = AdminColors.TextMuted,
                    fontSize = 13.sp,
                    modifier = Modifier.padding(top = 4.dp),
                )

                if (!canAvisar) {
                    Spacer(Modifier.height(8.dp))
                    Text(
                        "Para avisar, configura el correo (SMTP) o WhatsApp con la plantilla aprobada en " +
                            "Configuración → WhatsApp.",
                        color = AdminColors.TextMuted,
                        fontSize = 12.sp,
                    )
                }
                if (current.channels.emailPaused) {
                    Spacer(Modifier.height(8.dp))
                    PausedBanner(
                        nombre = "por correo",
                        canOperate = current.canOperate,
                        onResume = { resumeChannel(CANAL_CORREO) },
                    )
                }
                if (current.channels.whatsappPaused) {
                    Spacer(Modifier.height(8.dp))
                    PausedBanner(
                        nombre = "por WhatsApp",
                        canOperate = current.canOperate,
                        onResume = { resumeChannel(CANAL_WHATSAPP) },
                    )
                }
                notice?.let {
                    Spacer(Modifier.height(8.dp))
                    Text(it, color = AdminColors.Primary, fontSize = 13.sp)
                }
                actionError?.let {
                    Spacer(Modifier.height(8.dp))
                    Text(it, color = AdminColors.Error, fontSize = 13.sp)
                }
                progress?.let { p ->
                    Spacer(Modifier.height(8.dp))
                    Text(
                        "Cola de avisos: enviados ${p.sent} de ${p.total} · fallidos ${p.failed} · " +
                            "pendientes ${p.pending}" + if (p.done) " (terminado)" else "",
                        color = AdminColors.TextMuted,
                        fontSize = 12.sp,
                    )
                }

                Spacer(Modifier.height(16.dp))
                TermsKpiRow(activos)
                Spacer(Modifier.height(16.dp))
                TermsDirectoryCard(
                    members = filtrados,
                    query = query,
                    onQueryChange = { query = it },
                    filter = filter,
                    onFilterChange = { filter = it },
                    pendientesCount = pendientes.size,
                    canAvisar = canAvisar,
                    canOperate = current.canOperate,
                    busyId = busyId,
                    onAvisarPendientes = {
                        noticeScopePendientes = true
                        noticeTargets = pendientes
                    },
                    onReactivate = { run(it, "REACTIVATE") },
                    onResend = { run(it, "RESEND") },
                    onSuspend = { suspendTarget = it },
                    onNotify = {
                        noticeScopePendientes = false
                        noticeTargets = listOf(it)
                    },
                )
            }
        }
    }
}

private fun TermsFilter.incluye(r: AdminClientTermsRow): Boolean = when (this) {
    TermsFilter.TODOS -> true
    TermsFilter.PENDIENTES -> r.status == "PENDIENTE"
    TermsFilter.ACEPTADOS -> r.status == "ACEPTADO"
    TermsFilter.RECHAZADOS -> r.status == "RECHAZADO"
    TermsFilter.SUSPENDIDOS -> r.status == "SUSPENDIDO"
}

private fun coincide(r: AdminClientTermsRow, query: String): Boolean {
    val q = query.trim().lowercase()
    if (q.isEmpty()) return true
    return listOfNotNull(r.name, r.email, r.phone).any { it.lowercase().contains(q) }
}

@Composable
private fun TermsKpiRow(activos: List<AdminClientTermsRow>) {
    val items = listOf(
        Triple("Socios activos", activos.size.toString(), AdminColors.Primary),
        Triple("Aceptaron", activos.count { it.status == "ACEPTADO" }.toString(), Color(0xFF2E7D32)),
        Triple("Pendientes", activos.count { it.status == "PENDIENTE" }.toString(), AdminColors.TextMuted),
        Triple(
            "Suspendidos",
            activos.count { it.status == "RECHAZADO" || it.status == "SUSPENDIDO" }.toString(),
            AdminColors.Error,
        ),
    )
    BoxWithConstraints(Modifier.fillMaxWidth()) {
        if (maxWidth < COMPACT_BREAKPOINT) {
            Column(verticalArrangement = Arrangement.spacedBy(10.dp)) {
                items.chunked(2).forEach { row ->
                    Row(horizontalArrangement = Arrangement.spacedBy(10.dp), modifier = Modifier.fillMaxWidth()) {
                        row.forEach { (label, value, accent) ->
                            TermsKpiCard(label, value, accent, Modifier.weight(1f))
                        }
                    }
                }
            }
        } else {
            Row(horizontalArrangement = Arrangement.spacedBy(12.dp), modifier = Modifier.fillMaxWidth()) {
                items.forEach { (label, value, accent) ->
                    TermsKpiCard(label, value, accent, Modifier.weight(1f))
                }
            }
        }
    }
}

@Composable
private fun TermsKpiCard(label: String, value: String, accent: Color, modifier: Modifier = Modifier) {
    Column(
        modifier
            .clip(RoundedCornerShape(14.dp))
            .background(AdminColors.SurfaceContainer)
            .padding(16.dp),
    ) {
        Text(label, color = AdminColors.TextMuted, fontSize = 12.sp)
        Spacer(Modifier.height(6.dp))
        Text(value, fontWeight = FontWeight.Bold, fontSize = 22.sp, color = accent)
    }
}

@Composable
private fun TermsDirectoryCard(
    members: List<AdminClientTermsRow>,
    query: String,
    onQueryChange: (String) -> Unit,
    filter: TermsFilter,
    onFilterChange: (TermsFilter) -> Unit,
    pendientesCount: Int,
    canAvisar: Boolean,
    canOperate: Boolean,
    busyId: String?,
    onAvisarPendientes: () -> Unit,
    onReactivate: (AdminClientTermsRow) -> Unit,
    onResend: (AdminClientTermsRow) -> Unit,
    onSuspend: (AdminClientTermsRow) -> Unit,
    onNotify: (AdminClientTermsRow) -> Unit,
) {
    val listPaging = rememberAdminListPaging(
        initialSortKey = "name",
        resetKey = listOf(query, filter),
    )
    val pageItems = remember(members, listPaging.sort, listPaging.page) {
        members.adminSortedPaged(listPaging.sort, listPaging.page) { key ->
            when (key) {
                "status" -> { r -> r.status }
                else -> { r -> r.name.lowercase() }
            }
        }
    }

    BoxWithConstraints(
        Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(16.dp))
            .background(AdminColors.SurfaceContainer)
            .padding(16.dp),
    ) {
        val compact = maxWidth < COMPACT_BREAKPOINT
        Column(Modifier.fillMaxWidth()) {
            if (compact) {
                // Celular: título, buscador y avisos a ancho completo; cada socio es una tarjeta (como Clientes).
                Column(verticalArrangement = Arrangement.spacedBy(10.dp)) {
                    Text("Directorio de socios", fontWeight = FontWeight.Bold, fontSize = 16.sp, color = AdminColors.Text)
                    TermsSearchField(query, onQueryChange)
                    if (canAvisar) Button(
                        onClick = onAvisarPendientes,
                        enabled = pendientesCount > 0,
                        modifier = Modifier.fillMaxWidth().height(44.dp),
                        shape = RoundedCornerShape(10.dp),
                        colors = ButtonDefaults.buttonColors(
                            containerColor = AdminColors.Primary,
                            contentColor = AdminColors.OnBrand,
                        ),
                    ) { Text("Avisar pendientes ($pendientesCount)", fontWeight = FontWeight.SemiBold) }
                }
            } else {
                Row(
                    Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    Text("Directorio de socios", fontWeight = FontWeight.Bold, fontSize = 16.sp, color = AdminColors.Text)
                    Box(Modifier.widthIn(max = 260.dp)) {
                        TermsSearchField(query, onQueryChange)
                    }
                }
            }

            Spacer(Modifier.height(12.dp))
            Row(
                Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Row(
                    Modifier.weight(1f).horizontalScroll(rememberScrollState()),
                    horizontalArrangement = Arrangement.spacedBy(6.dp),
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    TermsFilter.entries.forEach { f ->
                        if (f == filter) {
                            Button(
                                onClick = { onFilterChange(f) },
                                shape = RoundedCornerShape(20.dp),
                                colors = ButtonDefaults.buttonColors(
                                    containerColor = AdminColors.Primary,
                                    contentColor = AdminColors.OnBrand,
                                ),
                            ) { Text(f.label, fontSize = 12.sp) }
                        } else {
                            OutlinedButton(
                                onClick = { onFilterChange(f) },
                                shape = RoundedCornerShape(20.dp),
                            ) { Text(f.label, fontSize = 12.sp, color = AdminColors.Text) }
                        }
                    }
                }
                if (canAvisar && !compact) {
                    Spacer(Modifier.width(10.dp))
                    Button(
                        onClick = onAvisarPendientes,
                        enabled = pendientesCount > 0,
                        shape = RoundedCornerShape(10.dp),
                        colors = ButtonDefaults.buttonColors(
                            containerColor = AdminColors.Primary,
                            contentColor = AdminColors.OnBrand,
                        ),
                    ) { Text("Avisar pendientes ($pendientesCount)", fontSize = 12.sp) }
                }
            }
            Spacer(Modifier.height(14.dp))

            if (members.isEmpty()) {
                Text("No hay socios en este filtro.", color = AdminColors.TextMuted, fontSize = 13.sp)
            } else if (compact) {
                Column(Modifier.fillMaxWidth(), verticalArrangement = Arrangement.spacedBy(10.dp)) {
                    pageItems.forEach { row ->
                        TermsMobileCard(
                            row = row,
                            canOperate = canOperate,
                            canAvisar = canAvisar,
                            busy = busyId == row.id,
                            onReactivate = { onReactivate(row) },
                            onResend = { onResend(row) },
                            onSuspend = { onSuspend(row) },
                            onNotify = { onNotify(row) },
                        )
                    }
                }
                TermsPagination(members.size, listPaging)
            } else {
                Column(Modifier.fillMaxWidth()) {
                    Row(
                        Modifier.fillMaxWidth().padding(vertical = 8.dp, horizontal = 4.dp),
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.spacedBy(4.dp),
                    ) {
                        AdminSortableHeader("Socio", "name", listPaging.sort, listPaging::onSort, Modifier.weight(1.6f))
                        AdminSortableHeader("Estado", "status", listPaging.sort, listPaging::onSort, Modifier.weight(1f))
                        AdminListHeaderLabel("Detalle", Modifier.weight(1.6f))
                        AdminListHeaderLabel("Acciones", Modifier.width(184.dp))
                    }
                    HorizontalDivider(color = AdminColors.Outline.copy(alpha = 0.5f))
                    pageItems.forEach { row ->
                        TermsMemberRow(
                            row = row,
                            canOperate = canOperate,
                            canAvisar = canAvisar,
                            busy = busyId == row.id,
                            onReactivate = { onReactivate(row) },
                            onResend = { onResend(row) },
                            onSuspend = { onSuspend(row) },
                            onNotify = { onNotify(row) },
                        )
                        HorizontalDivider(color = AdminColors.Outline.copy(alpha = 0.35f))
                    }
                }
                TermsPagination(members.size, listPaging)
            }
        }
    }
}

@Composable
private fun TermsPagination(totalCount: Int, listPaging: AdminListPaging) {
    AdminListPaginationBar(
        totalItems = totalCount,
        page = listPaging.page,
        onPageChange = { listPaging.page = it },
    )
}

@Composable
private fun TermsSearchField(query: String, onQueryChange: (String) -> Unit) {
    OutlinedTextField(
        value = query,
        onValueChange = onQueryChange,
        modifier = Modifier.fillMaxWidth(),
        singleLine = true,
        placeholder = { Text("Buscar socio...", color = AdminColors.TextMuted) },
        shape = RoundedCornerShape(10.dp),
        colors = OutlinedTextFieldDefaults.colors(
            focusedBorderColor = AdminColors.Primary,
            unfocusedBorderColor = AdminColors.Outline,
        ),
    )
}

@Composable
private fun TermsMemberRow(
    row: AdminClientTermsRow,
    canOperate: Boolean,
    canAvisar: Boolean,
    busy: Boolean,
    onReactivate: () -> Unit,
    onResend: () -> Unit,
    onSuspend: () -> Unit,
    onNotify: () -> Unit,
) {
    Row(
        Modifier.fillMaxWidth().padding(vertical = 10.dp, horizontal = 4.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(4.dp),
    ) {
        Column(Modifier.weight(1.6f)) {
            Text(row.name, fontWeight = FontWeight.SemiBold, color = AdminColors.Text)
            Text(contactoDe(row), color = AdminColors.TextMuted, fontSize = 12.sp)
        }
        Box(Modifier.weight(1f)) { TermsStatusBadge(row.status) }
        Text(termsDetail(row), Modifier.weight(1.6f), color = AdminColors.TextMuted, fontSize = 12.sp)
        Row(Modifier.width(184.dp), verticalAlignment = Alignment.CenterVertically) {
            TermsActionIcons(row, canOperate, canAvisar, busy, onReactivate, onResend, onSuspend, onNotify)
        }
    }
}

/** Tarjeta de socio para celular (mismo estilo que las tarjetas de Clientes). */
@Composable
private fun TermsMobileCard(
    row: AdminClientTermsRow,
    canOperate: Boolean,
    canAvisar: Boolean,
    busy: Boolean,
    onReactivate: () -> Unit,
    onResend: () -> Unit,
    onSuspend: () -> Unit,
    onNotify: () -> Unit,
) {
    Column(
        Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(12.dp))
            .border(1.dp, AdminColors.Outline.copy(alpha = 0.6f), RoundedCornerShape(12.dp))
            .padding(14.dp),
    ) {
        Text(row.name, fontWeight = FontWeight.SemiBold, color = AdminColors.Text)
        Text(contactoDe(row), color = AdminColors.TextMuted, fontSize = 12.sp)
        Spacer(Modifier.height(8.dp))
        TermsStatusBadge(row.status)
        Spacer(Modifier.height(6.dp))
        Text(termsDetail(row), color = AdminColors.TextMuted, fontSize = 12.sp)
        Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.End, verticalAlignment = Alignment.CenterVertically) {
            TermsActionIcons(row, canOperate, canAvisar, busy, onReactivate, onResend, onSuspend, onNotify)
        }
    }
}

/** Iconos de acción según estado y permisos. Igual en tabla y en tarjetas. */
@Composable
private fun TermsActionIcons(
    row: AdminClientTermsRow,
    canOperate: Boolean,
    canAvisar: Boolean,
    busy: Boolean,
    onReactivate: () -> Unit,
    onResend: () -> Unit,
    onSuspend: () -> Unit,
    onNotify: () -> Unit,
) {
    val suspended = row.status == "RECHAZADO" || row.status == "SUSPENDIDO"
    if (canAvisar && row.status != "ACEPTADO") {
        TermsIconAction("Avisar", !busy, onNotify, Icons.Outlined.Notifications, AdminColors.Primary)
    }
    if (canOperate && row.status != "PENDIENTE") {
        TermsIconAction("Reenviar términos", !busy, onResend, Icons.Outlined.Refresh, AdminColors.Primary)
    }
    if (canOperate && suspended) {
        TermsIconAction("Reactivar", !busy, onReactivate, Icons.Outlined.CheckCircle, AdminColors.Primary)
    }
    if (canOperate && !suspended) {
        TermsIconAction("Suspender", !busy, onSuspend, Icons.Outlined.Block, AdminColors.Error)
    }
}

/** Icono de acción con tooltip al pasar el cursor. El mismo texto sirve de nombre accesible. */
@OptIn(ExperimentalMaterial3Api::class)
@Composable
private fun TermsIconAction(
    label: String,
    enabled: Boolean,
    onClick: () -> Unit,
    icon: ImageVector,
    tint: Color,
) {
    TooltipBox(
        positionProvider = TooltipDefaults.rememberPlainTooltipPositionProvider(),
        tooltip = { PlainTooltip { Text(label) } },
        state = rememberTooltipState(),
    ) {
        IconButton(onClick = onClick, enabled = enabled) {
            Icon(icon, contentDescription = label, tint = tint)
        }
    }
}

@Composable
private fun TermsStatusBadge(status: String) {
    val (label, color) = when (status) {
        "ACEPTADO" -> "Aceptó" to AdminColors.Primary
        "PENDIENTE" -> "Pendiente" to AdminColors.TextMuted
        "RECHAZADO" -> "Rechazó" to AdminColors.Error
        "SUSPENDIDO" -> "Suspendido" to AdminColors.Error
        else -> status to AdminColors.TextMuted
    }
    Text(
        label,
        modifier = Modifier
            .clip(RoundedCornerShape(6.dp))
            .background(color.copy(alpha = 0.12f))
            .padding(horizontal = 8.dp, vertical = 4.dp),
        color = color,
        fontWeight = FontWeight.SemiBold,
        fontSize = 12.sp,
    )
}

/** Diálogo para elegir canales. Solo se ofrecen los que están listos (SMTP o plantilla de WhatsApp aprobada). */
@Composable
private fun NoticeDialog(
    count: Int,
    pendientes: Boolean,
    channels: AdminTermsNoticeChannels?,
    onConfirm: (List<String>) -> Unit,
    onDismiss: () -> Unit,
) {
    var correo by remember { mutableStateOf(channels?.email == true) }
    var whatsapp by remember { mutableStateOf(channels?.whatsapp == true) }
    val seleccion = listOfNotNull(
        CANAL_CORREO.takeIf { correo && channels?.email == true },
        CANAL_WHATSAPP.takeIf { whatsapp && channels?.whatsapp == true },
    )
    AlertDialog(
        onDismissRequest = onDismiss,
        title = {
            Text(
                if (pendientes) "Avisar a $count socio(s) pendientes" else "Avisar al socio",
                fontWeight = FontWeight.Bold,
            )
        },
        text = {
            Column {
                Text(
                    "Recibirá un aviso para que lea y responda los términos desde el menú Términos del portal.",
                    fontSize = 13.sp,
                )
                Spacer(Modifier.height(10.dp))
                if (channels?.email == true) {
                    Row(verticalAlignment = Alignment.CenterVertically) {
                        Checkbox(checked = correo, onCheckedChange = { correo = it })
                        Text("Correo", fontSize = 14.sp)
                    }
                }
                if (channels?.whatsapp == true) {
                    Row(verticalAlignment = Alignment.CenterVertically) {
                        Checkbox(checked = whatsapp, onCheckedChange = { whatsapp = it })
                        Text("WhatsApp", fontSize = 14.sp)
                    }
                }
                Spacer(Modifier.height(8.dp))
                Text(
                    "Los avisos se envían por cola y no se repiten al mismo socio dentro del plazo configurado en Empresa.",
                    color = GymlyColors.TextMuted,
                    fontSize = 12.sp,
                )
            }
        },
        confirmButton = {
            Button(onClick = { onConfirm(seleccion) }, enabled = seleccion.isNotEmpty()) {
                Text("Encolar aviso")
            }
        },
        dismissButton = { TextButton(onClick = onDismiss) { Text("Cancelar") } },
    )
}

/** Aviso de canal pausado por fallos seguidos. Solo quien opera el monitoreo puede reanudarlo. */
@Composable
private fun PausedBanner(nombre: String, canOperate: Boolean, onResume: () -> Unit) {
    Column(Modifier.fillMaxWidth()) {
        Text(
            "Avisos $nombre pausados: fallaron los últimos $PAUSA_TRAS_FALLOS envíos seguidos. " +
                "Revisa la configuración antes de reanudar.",
            color = AdminColors.Error,
            fontSize = 13.sp,
        )
        if (canOperate) {
            Spacer(Modifier.height(6.dp))
            Button(onClick = onResume) { Text("Reanudar", fontSize = 12.sp) }
        }
    }
}

private fun contactoDe(r: AdminClientTermsRow): String =
    listOfNotNull(r.email, r.phone).joinToString(" · ").ifEmpty { "Sin correo ni teléfono" }

private fun termsDetail(r: AdminClientTermsRow): String = when (r.status) {
    "ACEPTADO" -> "Versión ${r.termsVersion ?: "—"}${r.termsAcceptedAt?.let { " · ${it.take(10)}" } ?: ""}"
    "RECHAZADO" -> "Rechazó la versión ${r.declinedVersion ?: "—"}${r.declinedAt?.let { " · ${it.take(10)}" } ?: ""}"
    "SUSPENDIDO" -> "Motivo: ${r.suspensionNote ?: "—"}"
    else -> if (r.portalAccess) "Aún no responde a la versión vigente" else "Sin acceso al portal todavía"
}
